// real-image component (img when src, branded gradient fallback) — replaces the DS seeded-mock Photo;
// ApartmentCard (R4) consumes it with real coverPhoto URLs.

import { useCallback, useState, type HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { MAX_WIDTH, transformedSrc, transformedSrcSet } from '@/lib/img';

export interface PhotoProps extends HTMLAttributes<HTMLDivElement> {
  src?: string;
  alt?: string;       // applied to the <img> when src present (default '')
  label?: string;     // bottom-left caption overlay
  dim?: number;       // 0..1 dark overlay opacity (default 0)
  rounded?: number | string;  // inline borderRadius
  seed?: number;      // fallback gradient variety (default 0)
  /** Above the fold: load eagerly at high fetch priority (LCP candidate). */
  priority?: boolean;
  /** `sizes` attribute — how wide this renders at each breakpoint. */
  sizes?: string;
  /** Request exactly this width and skip the srcset (thumbnails). */
  fixedWidth?: number;
  /** Intrinsic size of the original, from the API. */
  width?: number;
  height?: number;
  /**
   * How the image fills its box. 'cover' (default) crops to fill — right for
   * cards and hero slots. 'contain' shows the whole frame — right for a
   * lightbox, where cropping the picture the guest opened to look at would
   * defeat the point.
   */
  fit?: 'cover' | 'contain';
  /**
   * Show a spinner over the box until the image has loaded.
   *
   * Opt-in, and only for photos a guest is actually looking at: on a
   * thumbnail the strip's own layout already says something is coming, and
   * twenty spinners in a row is worse than twenty empty boxes. On a
   * full-size photo the branded gradient behind an unloaded image reads as a
   * photo that happens to look like a gradient, which is the confusion this
   * removes.
   */
  indicateLoading?: boolean;
}

const GRADIENTS = [
  'linear-gradient(135deg, #2a4a6b, #16304a)',
  'linear-gradient(135deg, #b9a06a, #8a6f3c)',
  'linear-gradient(135deg, #355b73, #1d3a52)',
  'linear-gradient(135deg, #bfa676, #877044)',
  'linear-gradient(135deg, #3a5a78, #20405e)',
  'linear-gradient(135deg, #a98f5e, #6f5a32)',
];

export function Photo({
  src,
  alt,
  label,
  dim = 0,
  rounded,
  seed = 0,
  priority,
  sizes,
  fixedWidth,
  width,
  height,
  fit = 'cover',
  indicateLoading = false,
  className,
  children,
  style,
  ...rest
}: PhotoProps) {
  const gradient = GRADIENTS[seed % GRADIENTS.length];
  // Scoped to the src it failed for, not the component instance: a gallery's
  // main-photo slot keeps one Photo mounted while src changes as the guest
  // clicks thumbnails, so a stale boolean would permanently pin that slot to
  // untransformed originals after a single transient failure.
  const [failedSrc, setFailedSrc] = useState<string>();
  const failed = failedSrc !== undefined && failedSrc === src;

  // Keyed by src for the same reason failedSrc is: one Photo instance serves a
  // slot whose src changes, and a plain boolean would report the PREVIOUS
  // photo's load as this one's.
  const [loadedSrc, setLoadedSrc] = useState<string>();
  const settled = !src || loadedSrc === src;

  // A cached image can be complete before React attaches onLoad, and then the
  // event never fires — the spinner would sit on top of a photo that is
  // already there. The ref callback checks for that on attach.
  const imgRef = useCallback(
    (node: HTMLImageElement | null) => {
      if (node?.complete && src) setLoadedSrc(src);
    },
    [src],
  );

  const srcSet = !failed && src && fixedWidth === undefined
    ? transformedSrcSet(src)
    : undefined;
  const resolvedSrc = !failed && src
    ? transformedSrc(src, fixedWidth ?? MAX_WIDTH)
    : src;

  return (
    <div
      className={cn('relative h-full w-full overflow-hidden', className)}
      style={{
        background: gradient,
        borderRadius: rounded,
        ...style,
      }}
      {...rest}
    >
      {src && (
        <img
          src={resolvedSrc}
          // An empty srcset is omitted entirely — a dev URL has nothing to
          // offer the browser here.
          srcSet={srcSet || undefined}
          sizes={srcSet ? sizes : undefined}
          width={width}
          height={height}
          alt={alt ?? ''}
          className={cn(
            'absolute inset-0 h-full w-full',
            fit === 'contain' ? 'object-contain' : 'object-cover',
          )}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : undefined}
          // Transformations being off at the zone yields a bare 404 that
          // onerror=redirect cannot rescue. Fall back to the original once,
          // so a misconfiguration degrades to "slow" and not to "no photos".
          ref={imgRef}
          onLoad={() => setLoadedSrc(src)}
          onError={() => {
            // Already on the fallback and it failed too: stop indicating. A
            // spinner that never resolves is a worse answer than the empty
            // branded box, which at least stops asking the guest to wait.
            if (failed) setLoadedSrc(src);
            else setFailedSrc(src);
          }}
        />
      )}
      {indicateLoading && !settled && (
        <span
          data-testid="photo-loading"
          role="status"
          aria-label="Loading photo"
          // pointer-events-none is load-bearing, not tidiness: this covers the
          // whole box, and the carousel's prev/next sit on top of the photo.
          // Without it the indicator swallows their clicks for as long as the
          // photo takes to arrive — which is exactly when a guest reaches for
          // the arrow.
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
        >
          <span
            aria-hidden="true"
            className="h-7 w-7 animate-spin rounded-full border-2 border-white/35 border-t-white/90 motion-reduce:animate-none"
          />
        </span>
      )}
      {dim > 0 && (
        <div
          className="absolute inset-0"
          data-dim
          style={{ background: `rgba(15,23,42,${dim})` }}
        />
      )}
      {label && (
        <span
          className="absolute bottom-2.5 left-3 z-[2] text-[11px] font-semibold tracking-[0.04em] text-white/85"
          style={{ textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}
        >
          {label}
        </span>
      )}
      {children}
    </div>
  );
}
